import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PiXBold } from 'react-icons/pi';
import { Button, LoadingState, Title } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import CdRackGrid from './CdRackGrid';
import type { CdRuaGrid, CdSlot } from './CdRackGrid';
import CdSlotDetailPanel from './CdSlotDetailPanel';

type ApiData = {
    ruas: CdRuaGrid[];
    summary?: {
        total_slots: number;
        occupied_slots: number;
        free_slots: number;
    };
};

type Props = {
    open: boolean;
    shelves: string[];
    onClose: () => void;
};

export default function CdLocationHighlightModal({ open, shelves, onClose }: Props) {
    const { t } = useI18n();
    const highlightCodes = useMemo(
        () => shelves.map((s) => s.trim().toUpperCase()).filter(Boolean),
        [shelves],
    );
    const [selectedSlotCode, setSelectedSlotCode] = useState<string | null>(null);

    const { data, isLoading } = useQuery<ApiData>({
        queryKey: ['dashboard', 'estoque', 'consult-highlight'],
        enabled: open && highlightCodes.length > 0,
        queryFn: async () => (await api.get('/dashboard/estoque')).data,
        staleTime: 30_000,
    });

    useEffect(() => {
        if (!open) {
            setSelectedSlotCode(null);
            return;
        }
        if (highlightCodes.length > 0) {
            setSelectedSlotCode(highlightCodes[0]);
        }
    }, [open, highlightCodes]);

    const visibleRuas = useMemo(() => {
        const ruas = data?.ruas ?? [];
        if (highlightCodes.length === 0) {
            return ruas;
        }
        return ruas.filter((rua) =>
            highlightCodes.some((code) => code.startsWith(rua.code) || code.charAt(0) === rua.code),
        );
    }, [data?.ruas, highlightCodes]);

    if (!open) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-3 sm:items-center sm:p-6">
            <div
                className="absolute inset-0"
                onClick={onClose}
                aria-hidden
            />
            <div
                role="dialog"
                aria-modal="true"
                className="relative z-[1] flex max-h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
            >
                <header className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                    <div>
                        <Title as="h3" className="text-base sm:text-lg">
                            {t('v2.stock_consult.view_on_map')}
                        </Title>
                        <p className="mt-1 font-mono text-sm font-semibold text-amber-700 dark:text-amber-300">
                            {highlightCodes.join(' · ')}
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={onClose} aria-label="Close">
                        <PiXBold className="size-4" />
                    </Button>
                </header>

                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
                    {isLoading && <LoadingState variant="cards" />}

                    {!isLoading && visibleRuas.length === 0 && (
                        <p className="py-10 text-center text-sm text-gray-500">
                            {t('v2.stock_consult.no_location')}
                        </p>
                    )}

                    {visibleRuas.map((rua) => (
                        <section
                            key={rua.code}
                            className="rounded-2xl border border-amber-200/80 bg-white p-4 shadow-sm dark:border-amber-900/50 dark:bg-gray-900"
                        >
                            <div className="mb-3 flex items-end justify-between gap-2">
                                <div>
                                    <h4 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                        {rua.label}
                                    </h4>
                                    <p className="text-xs text-gray-500">6 colunas · 3 andares</p>
                                </div>
                                <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
                                    Rua {rua.code}
                                </span>
                            </div>
                            <CdRackGrid
                                rua={rua}
                                variant="compact"
                                solidColors
                                highlightCodes={highlightCodes}
                                selectedCode={selectedSlotCode}
                                onSlotClick={(slot: CdSlot) => setSelectedSlotCode(slot.code)}
                            />
                        </section>
                    ))}

                    {selectedSlotCode && (
                        <CdSlotDetailPanel
                            slotCode={selectedSlotCode}
                            onClose={() => setSelectedSlotCode(null)}
                            warehouseSummary={
                                data?.summary
                                    ? {
                                          total_slots: data.summary.total_slots,
                                          occupied_slots: data.summary.occupied_slots,
                                          free_slots: data.summary.free_slots,
                                          ruas_count: visibleRuas.length,
                                      }
                                    : undefined
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
